import { useRef, useState } from 'react';
import { Check, Copy, Upload } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const BACKUP_FILE_NAME = '3d0d7e5fb2ce288813306e4d4636395e047a3d28';

type IphoneSmsGuideProps = {
  readonly onFileSelected: (file: File) => void;
  readonly isPending: boolean;
  readonly errorMessage?: string;
};

export function IphoneSmsGuide({ onFileSelected, isPending, errorMessage }: IphoneSmsGuideProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(BACKUP_FILE_NAME);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileSelected(file);
    }
    event.currentTarget.value = '';
  };

  return (
    <div className="rounded-md border border-white/[0.08] p-3">
      <p className="text-xs leading-relaxed text-[#8b8d9a]">
        Ta en lokal, ukryptert backup av iPhone i Finder/iTunes. Finn meldingsdatabasen i
        backup-mappen under <span className="text-[#ededf0]">3d/</span> – det er filen med navnet
        under. Kopiér den, gi den gjerne navnet <span className="text-[#ededf0]">sms.db</span>, og
        last den opp her.
      </p>
      <p className="mt-2 text-[11px] leading-relaxed text-[#8b8d9a]">
        <span className="text-warning">Merk:</span> krypterte (passordbeskyttede) backuper støttes
        ikke ennå – fjern kryptering i Finder før du tar backupen.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <code className="flex-1 truncate rounded bg-[#1a1b23] px-2 py-1.5 text-[11px] text-[#ededf0]">
          {BACKUP_FILE_NAME}
        </code>
        <Button
          className="shrink-0 px-2 py-1.5 text-xs"
          variant="outline"
          type="button"
          onClick={handleCopy}
          aria-label="Kopiér filnavn"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Kopiert' : 'Kopiér'}
        </Button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".db,application/x-sqlite3"
        className="hidden"
        onChange={handleChange}
      />
      <Button
        className="mt-3 w-full"
        variant="primary"
        type="button"
        disabled={isPending}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload size={14} />
        {isPending ? 'Laster opp og analyserer...' : 'Velg sms.db-fil'}
      </Button>
      {errorMessage && <p className="mt-2 text-xs text-danger/80">{errorMessage}</p>}
    </div>
  );
}
